'use client';

import { type ReactNode, useEffect, useState } from 'react';

import dynamic from 'next/dynamic';

import type { CreatorInfo } from '@ps/types/creator';
import { useCurrentUserId } from '@ps/ui/contexts/auth';

import type { BioAccessGuard } from './guard-descriptor';
import { useClaimLeadContact } from './hooks/use-claim-lead-contact';
import {
  type GuestContactData,
  useCreateGuestAndUpdateInfo,
} from './hooks/use-create-guest';
import { useLeadFormGuardContext } from './lead-form-guard-context';

// Every guard modal is interaction-only: `AccessGuard` renders for EVERY gated
// bio button, but a modal appears only on an explicit tap. Static imports would
// bundle all four (and their deps — the guest-contact / lead-form modals alone
// pull `PhoneInput` → `CountryPicker` → the full country table, plus
// react-hook-form / recaptcha) into the eager visitor-chrome first-load. So each
// code-splits into its own chunk that loads ONLY once its gate is requested (see
// the `openedModals` gate below — a plain `dynamic()` still loads on mount, since
// the modal is always in the tree with `open={false}`).
const LeadFormModal = dynamic(
  () => import('./modals/lead-form-modal').then(module => module.LeadFormModal),
  { ssr: false },
);
const GuestContactModal = dynamic(
  () =>
    import('./modals/guest-contact-modal').then(
      module => module.GuestContactModal,
    ),
  { ssr: false },
);
const FollowGuardModal = dynamic(
  () =>
    import('./modals/follow-guard-modal').then(
      module => module.FollowGuardModal,
    ),
  { ssr: false },
);
const SubscribeConfirmModal = dynamic(
  () =>
    import('./modals/subscribe-confirm-modal').then(
      module => module.SubscribeConfirmModal,
    ),
  { ssr: false },
);

// ─── Types ────────────────────────────────────────────────────────────────

type ActiveModal =
  | 'none'
  | 'lead-form'
  | 'follow'
  | 'guest-contact'
  | 'subscribe-confirm';

// ─── Props ────────────────────────────────────────────────────────────────

export interface AccessGuardProps {
  storeId: string;
  /**
   * The single resolved access requirement (RN's `AccessGuardInfo`). Its `kind`
   * selects the gate; its base fields (`triggers`, `nonDismissible`, `source`,
   * `leadCapture`) tune it. Lead forms in `guard.triggers` are checked FIRST,
   * ahead of `kind`. See {@link BioAccessGuard}.
   */
  guard: BioAccessGuard;
  /** Seller profile for display in guard modals (avatar, name, bio). */
  sellerProfile: CreatorInfo;
  /**
   * Invoked when a `subscription`-kind guard gates the action (e.g. redirect to
   * the subscribe picker). Ignored for other kinds.
   */
  onSubscriptionRequired?: () => void;
  /**
   * The viewer's follow status, when the caller already has it — pass this to
   * keep the guard FIRESTORE-FREE. `undefined` = still resolving; `true`/`false`
   * = resolved. On the bio, `BioUserAccessProvider` owns this (lazy, opt-in
   * realtime), so the visitor path never statically loads `firebase/firestore`.
   *
   * When OMITTED (callers outside that provider — LPB booking/video), the guard
   * resolves follow status itself via a DYNAMIC firestore import — loaded only
   * if a `follow`-kind gate actually needs it, never eagerly bundled.
   */
  isFollowing?: boolean;
  /**
   * Whether the viewer's subscription standing has resolved
   * (`!loading` from `useBioUserAccess`). The tier check runs in the CALLER
   * (`downgradeIfTierHeld`), where "holds no tier" and "not resolved yet" are
   * the same `undefined` — so without this a viewer who just paid is asked to
   * subscribe again while the re-fetch is in flight. Defaults to `true` for
   * callers whose standing is synchronous.
   */
  isSubscriptionResolved?: boolean;
  /**
   * Fires after a follow API call SUCCEEDS (immediate or post-login resume) —
   * so a caller under `BioUserAccessProvider` can refresh the viewer's follow
   * standing. Distinct from `onAccessGranted`: the post-login resume completes
   * the follow when this guard has already unmounted, so `onAccessGranted`
   * (guard bookkeeping) can't cover it.
   */
  onFollowPersisted?: () => void;
  /**
   * Called automatically after the guard completes — use to proceed with the
   * action (e.g. booking). Receives the contact a gate collected (lead-form or
   * guest-contact), when any, so the caller can pre-fill a downstream form.
   */
  onAccessGranted?: (contactData?: GuestContactData) => void;
  /**
   * Called when any guard modal dismisses — includes backdrop tap,
   * escape key, unqualified lead-form result acknowledge, and
   * suggest-different-link branch.
   */
  onDismiss?: () => void;
  /**
   * Called only when the user explicitly taps the "Cancel" button in a
   * guard modal (abandon intent). Lead-form and guest-contact modals
   * both honor this; follow-guard has no Cancel button.
   */
  onCancel?: () => void;
  /** Render prop: (canProceed, requestAccess, isReady) */
  children: (
    canProceed: boolean,
    requestAccess: () => void,
    isReady: boolean,
  ) => ReactNode;
}

// ─── Component ────────────────────────────────────────────────────────────

/**
 * Access guard. Resolves the correct gate from the {@link BioAccessGuard}
 * descriptor and shows the appropriate modal before allowing the action.
 *
 * Checks past submissions first — if the user already qualified for a lead form
 * or already submitted a contact form, the guard is skipped.
 *
 * Priority order (same as RN):
 * 1. Owner → no guard
 * 2. Lead form (from `guard.triggers`) → skip if already qualified, else show it
 * 3. `guard.kind`:
 *    - `subscription`  → hand off to `onSubscriptionRequired`
 *    - `contact-info`  → guest-contact form (anon AND logged-in), skip if submitted
 *    - `skip-follow`   → logged-in proceeds; anonymous → guest-contact
 *    - `follow`        → anonymous → guest-contact; logged-in non-follower → follow modal
 */
export function AccessGuard({
  storeId,
  guard,
  onSubscriptionRequired,
  isFollowing: isFollowingProp,
  isSubscriptionResolved = true,
  onFollowPersisted,
  onAccessGranted,
  onDismiss,
  onCancel,
  children,
  sellerProfile,
}: AccessGuardProps) {
  // Destructure the descriptor into the flags the resolution below reads. This
  // is the ONE place the union is unpacked — the logic stays flat/readable.
  const { triggers = [], nonDismissible, leadCapture, source } = guard;
  // `none` = no restriction: proceed for EVERYONE (incl. anonymous) with no
  // modal, once any lead form in `triggers` clears — RN's `guard == undefined`.
  // Distinct from `skip-follow`, which guest-contacts anonymous viewers.
  const noGate = guard.kind === 'none';
  const skipFollowGuard = guard.kind === 'skip-follow';
  const subscriptionRequired = guard.kind === 'subscription';
  const contactInfoRequired = guard.kind === 'contact-info';
  // A `follow` gate (FollowMe, follower-restricted buttons) routes EVERYONE —
  // anonymous included — to the follow modal, NOT the guest-contact form. RN
  // shows the follow-confirm modal to anon too and gates the follow behind login
  // on confirm (`withAuthenticated`); it never guest-contacts a follow button.
  // Only `skip-follow` / `contact-info` (Calendar / RE / DigitalProduct) send an
  // anonymous viewer to the guest-contact form.
  const isFollowKind = guard.kind === 'follow';
  // RN's Calendar booking gate is a `follow` guard that, for ANONYMOUS viewers
  // only, collects contact info (creating a guest) instead of showing the follow
  // modal — while a logged-in non-follower still gets "Follow Me". This flag opts
  // a `follow` guard into that anonymous-only behavior; logged-in resolution is
  // unchanged (still the follow modal).
  const followAnonContactInfo =
    guard.kind === 'follow' && guard.anonymousContactInfo === true;

  const userId = useCurrentUserId();
  const isOwner = userId === storeId;
  const isAnonymous = !userId;
  const {
    getGuard,
    isLoaded: leadFormsLoaded,
    isLoadingSubmissions,
    hasQualifiedForForm,
    markFormQualified,
    isFormUnqualified,
    markFormUnqualified,
    hasSubmittedContactForm,
    markContactFormSubmitted,
  } = useLeadFormGuardContext();

  const [activeModal, setActiveModal] = useState<ActiveModal>('none');
  // Which modals have been requested at least once. A lazily-imported modal is
  // only rendered after its gate first fires, so its chunk loads on the tap that
  // opens it — never on `AccessGuard` mount. Once opened it stays mounted (kept
  // in this list) so the exit animation runs and a re-open is instant.
  const [openedModals, setOpenedModals] = useState<ActiveModal[]>([]);
  function openModal(modal: ActiveModal) {
    setActiveModal(modal);
    setOpenedModals(prev => (prev.includes(modal) ? prev : [...prev, modal]));
  }
  const [accessGrantedByGuard, setAccessGrantedByGuard] = useState(false);
  // Self-resolved follow status, used ONLY when the caller doesn't supply
  // `isFollowingProp` (e.g. LPB booking/video, outside `BioUserAccessProvider`).
  const [selfIsFollowing, setSelfIsFollowing] = useState<boolean | undefined>(
    undefined,
  );
  // Prefer the caller-supplied status (bio path — keeps this guard
  // firestore-free); otherwise the self-resolved value.
  const isFollowing = isFollowingProp ?? selfIsFollowing;

  // Only a `follow`-kind gate for a logged-in non-owner actually needs the
  // status — and only when the caller didn't already provide it. Everything
  // else (skip-follow, subscription, contact-info, anonymous, the bio path
  // supplying the prop) never touches Firestore here.
  const needsSelfFollowStatus =
    isFollowingProp === undefined &&
    isFollowKind &&
    !!userId &&
    !isOwner &&
    !isAnonymous;

  // Resolve follow status from Firestore ONLY when needed. `firebase/firestore`
  // is DYNAMICALLY imported so it never lands in the visitor bundle eagerly
  // (matches `BioUserAccessProvider`'s lazy model).
  //
  // TODO(visitor-firestore): eliminate this self-resolution entirely. It's only
  // reachable from the two non-bio `follow`-kind callers that don't pass
  // `isFollowing` — `WiredBookButton` (non-RE booking) and `VideoFooter`
  // (non-RE clip). Have BOTH pass `isFollowing` (e.g. via `useIsFollowing`, or
  // better a server/proxy check like `BioUserAccessProvider` uses) so the guard
  // becomes purely presentational and this effect + the dynamic firestore
  // import can be deleted — no Firestore on the public/visitor path at all.
  useEffect(() => {
    if (!needsSelfFollowStatus) return;

    let disposed = false;
    let unsub: (() => void) | undefined;
    void (async () => {
      const { doc, firestore, onSnapshot } =
        await import('@ps/functions/firebase/firestore');
      if (disposed) return;
      const ref = doc(firestore(), 'profiles', userId, 'followings', storeId);
      unsub = onSnapshot(ref, snap => {
        if (!disposed) setSelfIsFollowing(snap.exists());
      });
      if (disposed) unsub();
    })();
    return () => {
      disposed = true;
      unsub?.();
    };
  }, [needsSelfFollowStatus, userId, storeId]);

  // Resolve the matching lead form once per render — used for button state,
  // request flow, and modal rendering. Only meaningful after
  // `leadFormsLoaded === true`.
  const leadForm = leadFormsLoaded ? getGuard(triggers) : null;

  // The button is only "ready" once all async dependencies that could alter
  // the access decision have resolved. Owners bypass every guard, so they're
  // always ready.
  //  - lead forms fetched (so we know if a form gates this trigger)
  //  - past submissions fetched (so we know if the user already qualified —
  //    only relevant for logged-in users, since anonymous can't have prior
  //    submissions in the server)
  //  - follow status known for logged-in non-owners (so we don't show a
  //    follow modal to an already-following user, and vice versa)
  // Follow status only affects a `follow`-kind gate (whether to show the follow
  // modal vs. proceed). Every other kind (none / skip-follow / subscription /
  // contact-info) ignores it, so it must NOT block their readiness — otherwise a
  // logged-in viewer's DigitalProduct/contact-info button would hang forever
  // waiting on `isFollowing` and never open its form.
  const isLoggedInNonOwner = userId != null && !isOwner && !isAnonymous;
  const followStatusReady =
    !isLoggedInNonOwner || !isFollowKind || isFollowing !== undefined;
  // `followStatusReady`'s counterpart for the tier gate. An anonymous viewer
  // holds no tier and never will, so only a logged-in non-owner waits — the
  // anonymous subscribe prompt still opens on the first tap.
  const subscriptionStatusReady =
    !isLoggedInNonOwner || !subscriptionRequired || isSubscriptionResolved;
  const submissionsReady = isAnonymous || !isLoadingSubmissions;
  // Firebase auth loads lazily, so `userId` is `undefined` until it resolves.
  // The guard must NOT decide anything while unresolved — `isAnonymous`
  // (`!userId`) would be a false positive and route a signed-in viewer down the
  // anonymous branch. Gate readiness on a resolved auth state (owner bypasses,
  // but `isOwner` can't be true while `undefined` anyway). This also keeps the
  // triggering button disabled (consumers gate on `isReady`) until we know who
  // the viewer is.
  const authResolved = userId !== undefined;
  const isReady =
    authResolved &&
    (isOwner ||
      (leadFormsLoaded &&
        submissionsReady &&
        followStatusReady &&
        subscriptionStatusReady));

  function requestAccess() {
    if (isOwner || accessGrantedByGuard) return;

    // Wait until everything is ready before deciding which guard to show
    if (!isReady) return;

    // Lead form takes priority
    if (leadForm) {
      // Skip if user already qualified for this form
      if (hasQualifiedForForm(leadForm.id)) {
        setAccessGrantedByGuard(true);
        onAccessGranted?.();
        return;
      }
      // Already submitted but didn't qualify — blocked, don't show form again
      if (isFormUnqualified(leadForm.id)) return;
      openModal('lead-form');
      return;
    }

    // No restriction (RN `guard == undefined`): the lead form (if any) has
    // cleared above, so just proceed — for everyone, anonymous included. Never
    // guest-contacts.
    if (noGate) {
      setAccessGrantedByGuard(true);
      onAccessGranted?.();
      return;
    }

    // Subscription-tier gate (RN `subscribers`): a required-but-unheld tier
    // supersedes the anonymous guest-contact AND follow branches (a subscription
    // implies following, and even anonymous viewers subscribe rather than
    // guest-contact). Hand off to the caller (redirect to the subscribe picker).
    // Subscription gate: show the community-join intro modal first (RN's
    // `GuardBySubscription` → `GuardModal`); its Subscribe button hands off to
    // `onSubscriptionRequired` (the subscribe picker). We DON'T redirect
    // immediately — a guest would otherwise be bounced to /login mid-tap.
    if (subscriptionRequired) {
      openModal('subscribe-confirm');
      return;
    }

    // Contact-info gate (RN DigitalProduct `requireContactInfo`): show the
    // standalone contact form for BOTH anonymous and logged-in viewers, ahead
    // of the follow branch. Skip if this viewer already submitted it (the
    // `guest-contact` completion caches per-store via `hasSubmittedContactForm`,
    // shared across guards). The completion handler creates a guest for
    // anonymous viewers and records the contact for logged-in ones alike.
    if (contactInfoRequired) {
      if (hasSubmittedContactForm(storeId)) {
        setAccessGrantedByGuard(true);
        onAccessGranted?.();
        return;
      }
      openModal('guest-contact');
      return;
    }

    // Anonymous → guest contact form (skip if already submitted). NOT for a
    // plain `follow` gate: RN shows the follow modal (not guest-contact) to anon
    // and gates the follow behind login on confirm — so a follow button falls
    // through to the follow-modal branch below for everyone. EXCEPTION: a
    // `follow` guard with `anonymousContactInfo` (RN's Calendar booking) DOES
    // guest-contact the anonymous viewer here, while its logged-in path still
    // reaches the follow modal below.
    if (isAnonymous && (!isFollowKind || followAnonContactInfo)) {
      if (hasSubmittedContactForm(storeId)) {
        setAccessGrantedByGuard(true);
        onAccessGranted?.();
        return;
      }
      openModal('guest-contact');
      return;
    }

    // Logged-in: skip follow guard entirely when requested (RE listings)
    if (skipFollowGuard) {
      setAccessGrantedByGuard(true);
      onAccessGranted?.();
      return;
    }

    // Already following → no guard needed (logged-in only; an anon viewer is
    // never "following", so a follow button always reaches the modal).
    if (isFollowing) return;

    // Not following (or anonymous) → follow modal. For an anonymous viewer the
    // modal's confirm handles login before following (RN `withAuthenticated`).
    openModal('follow');
  }

  // Owner or already following = access granted; otherwise after completing a guard.
  // When skipFollowGuard is true, logged-in users can proceed as long as no
  // lead form is blocking them (RE listings don't require following).
  const leadFormBlocking =
    leadForm != null && !hasQualifiedForForm(leadForm.id);
  // A required-but-unheld subscription, or an unsatisfied contact-info gate,
  // blocks proceeding by any other means (a plain follow / guest-contact never
  // satisfies a subscription; a follow never satisfies contact-info). Only
  // completing the guard (accessGrantedByGuard) — or, for contact-info, having
  // already submitted this store's contact form — unblocks it.
  const contactInfoBlocking =
    contactInfoRequired === true &&
    !accessGrantedByGuard &&
    !hasSubmittedContactForm(storeId);
  const canProceed = isOwner
    ? true
    : // A blocking lead form is the HIGHEST-priority gate (RN resolves it ahead
      // of follow/subscription/contact; `requestAccess` checks it first). It must
      // veto EVERY other proceed path — including an already-following viewer.
      // Otherwise a DigitalProduct with an attached form navigates straight to
      // the PDP for anyone who follows the creator, skipping the form.
      leadFormBlocking && !accessGrantedByGuard
      ? false
      : subscriptionRequired && !accessGrantedByGuard
        ? false
        : contactInfoBlocking
          ? false
          : // No restriction → proceed for everyone (incl. anonymous), BUT only
            // once ready (`leadFormsLoaded`), so a button with a candidate lead-
            // form trigger doesn't navigate before we know a form gates it — the
            // race that let a DigitalProduct skip its attached form. `skip-follow`
            // still requires logged-in.
            (noGate && isReady) ||
            isFollowing === true ||
            accessGrantedByGuard ||
            (skipFollowGuard === true && !isAnonymous);

  const createGuestAndUpdate = useCreateGuestAndUpdateInfo(userId);
  const claimLeadContact = useClaimLeadContact();

  async function handleGuardComplete(
    contactData?: GuestContactData,
    leadContactId?: string,
  ) {
    // For anonymous users: create guest account with contact info.
    // (`createGuestAndUpdate` no-ops if a user already exists, so no extra
    // `currentUser` guard is needed here — keeping firebase/auth off the bundle.)
    if (isAnonymous && contactData) {
      await createGuestAndUpdate(contactData);
    }

    // Claim the just-submitted lead contact for the now-authenticated user
    // (real or just-created guest) so downstream actions (e.g. slot booking
    // confirmation mail) are attributed to the same user id. After the step
    // above, a user exists when the viewer was already signed in (`userId`) or
    // we just minted a guest for them (an anonymous viewer who supplied
    // `contactData`) — no need to read `auth().currentUser`.
    const hasAuthenticatedUser = userId != null || contactData != null;
    if (leadContactId && hasAuthenticatedUser) {
      await claimLeadContact(storeId, leadContactId);
    }

    // Optimistically cache the completion so the guard is skipped next time
    if (leadForm) {
      markFormQualified(leadForm.id);
    }
    // Cache the completion so the gate is skipped next time — for anonymous
    // viewers (the classic guest-contact path) and for the DigitalProduct
    // contact-info gate, which also fires for logged-in viewers.
    if (isAnonymous || contactInfoRequired) {
      markContactFormSubmitted(storeId);
    }

    setActiveModal('none');
    setAccessGrantedByGuard(true);

    // Defer booking so the modal exit animation completes first
    requestAnimationFrame(() => {
      onAccessGranted?.(contactData);
    });
  }

  function handleUnqualified() {
    if (leadForm) {
      markFormUnqualified(leadForm.id);
    }
  }

  function handleDismiss() {
    setActiveModal('none');
    onDismiss?.();
  }

  function handleCancel() {
    setActiveModal('none');
    onCancel?.();
  }

  return (
    <>
      {children(canProceed, requestAccess, isReady)}

      {/* Lead form modal */}
      {leadForm && openedModals.includes('lead-form') && (
        <LeadFormModal
          open={activeModal === 'lead-form'}
          form={leadForm}
          storeId={storeId}
          sellerProfile={sellerProfile}
          nonDismissible={nonDismissible}
          onQualified={handleGuardComplete}
          onUnqualified={handleUnqualified}
          onDismiss={handleDismiss}
          onCancel={onCancel ? handleCancel : undefined}
        />
      )}

      {/* Follow guard modal (non-follower + anonymous) */}
      {openedModals.includes('follow') && (
        <FollowGuardModal
          open={activeModal === 'follow'}
          storeId={storeId}
          sellerProfile={sellerProfile}
          nonDismissible={nonDismissible}
          copy={guard.kind === 'follow' ? guard.followCopy : undefined}
          onFollowed={handleGuardComplete}
          onFollowPersisted={onFollowPersisted}
          onAccessGranted={onAccessGranted}
          onDismiss={handleDismiss}
        />
      )}

      {/* Guest contact modal (anonymous users + DigitalProduct contact-info) */}
      {openedModals.includes('guest-contact') && (
        <GuestContactModal
          open={activeModal === 'guest-contact'}
          storeId={storeId}
          sellerProfile={sellerProfile}
          nonDismissible={nonDismissible}
          leadCapture={leadCapture}
          source={source}
          onComplete={handleGuardComplete}
          onDismiss={handleDismiss}
          onCancel={onCancel ? handleCancel : undefined}
        />
      )}

      {/* Community-join intro before the subscribe surface (subscription gate) */}
      {openedModals.includes('subscribe-confirm') && (
        <SubscribeConfirmModal
          open={activeModal === 'subscribe-confirm'}
          sellerProfile={sellerProfile}
          nonDismissible={nonDismissible}
          onConfirm={() => {
            setActiveModal('none');
            onSubscriptionRequired?.();
          }}
          onDismiss={handleDismiss}
        />
      )}
    </>
  );
}
